import { Action } from '../actions';
import { FolderApi } from '../api-typings/messages/messages_types';
import {
  firstReferenceFromReferenceList,
  mergeEntities,
  Path,
  pluckListItemFromRawStoreSlice,
  RawStoreListStateSlice,
  removeEntityById,
  transformObjects,
} from '../helpers';
import { Message } from '../message/message.model';
import { transformThread } from './thread.transformers';

export function threadsReducer(
  state: RawStoreListStateSlice<'threads'> = {},
  action: Action,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'threads') {
        const { result, refList } = action.payload;
        return mergeEntities<'threads'>(
          state,
          transformObjects(
            result.threads,
            transformThread,
            refList.pathList[1][0],
          ),
          firstReferenceFromReferenceList(refList),
        );
      }
      return state;
    }

    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'threads') {
        const { result, ref } = action.payload;
        return mergeEntities<'threads'>(
          state,
          transformObjects([result], transformThread, ref.path[1]),
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE': {
      if (action.payload.type === 'threads') {
        const { object: delta, originalObject, ref } = action.payload;
        return mergeEntities<'threads'>(
          state,
          transformObjects([originalObject.id.toString()], threadId => {
            const thread = pluckListItemFromRawStoreSlice(
              state,
              ref.path,
              threadId,
            );
            if (thread && delta.folder) {
              const folder: FolderApi = delta.folder || 'inbox';
              return { ...thread, folder };
            } else if (thread && delta.isRead) {
              return { ...thread, messageUnreadCount: 0, isRead: true };
            }
            return undefined;
          }),
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (
        action.payload.type === 'threads' &&
        action.payload.object.members !== undefined
      ) {
        const { object: delta, originalObject, ref } = action.payload;
        return mergeEntities<'threads'>(
          state,
          transformObjects([originalObject.id.toString()], threadId => {
            const thread = pluckListItemFromRawStoreSlice(
              state,
              ref.path,
              threadId,
            );
            if (thread) {
              return Object.assign(thread, delta);
            }
            return undefined;
          }),
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE_ERROR': {
      if (action.payload.type === 'threads') {
        const { object: delta, originalObject, ref } = action.payload;
        return mergeEntities<'threads'>(
          state,
          transformObjects([originalObject.id.toString()], threadId => {
            const thread = pluckListItemFromRawStoreSlice(
              state,
              ref.path,
              threadId,
            );
            if (thread && delta.folder) {
              const folder: FolderApi = originalObject.folder || 'inbox';
              return { ...thread, folder };
            }
            return undefined;
          }),
          ref,
        );
      }
      return state;
    }

    // TODO T37442 consider pulling this out somewhere else
    case 'WS_MESSAGE': {
      const { payload } = action;
      const userId = payload.toUserId;
      const path: Path<'threads'> = ['threads', userId];
      const ref = { path };
      if (
        payload.parent_type === 'messages' &&
        (payload.type === 'user_read' || payload.type === 'typing')
      ) {
        const fromUserId =
          payload.type === 'user_read'
            ? payload.data.user_id
            : payload.data.from_user;
        const threadIds =
          payload.type === 'user_read'
            ? payload.data.thread_ids
            : [payload.data.thread];

        if (fromUserId === parseInt(userId, 10)) {
          return mergeEntities<'threads'>(
            state,
            transformObjects(threadIds, threadId => {
              const thread = pluckListItemFromRawStoreSlice(
                state,
                path,
                threadId,
              );
              return thread
                ? { ...thread, message_unread_count: 0 }
                : undefined;
            }),
            ref,
          );
        }
        return mergeEntities<'threads'>(
          state,
          transformObjects(threadIds, threadId => {
            const thread = pluckListItemFromRawStoreSlice(
              state,
              path,
              threadId,
            );
            return thread
              ? {
                  ...thread,
                  userReadTimes: {
                    ...thread.userReadTimes,
                    [fromUserId]: Date.now(),
                  },
                }
              : undefined;
          }),
          ref,
        );
      } else if (
        payload.parent_type === 'messages' &&
        payload.type === 'private'
      ) {
        return mergeEntities<'threads'>(
          state,
          transformObjects([payload.data.thread_id], threadId => {
            const thread = pluckListItemFromRawStoreSlice(
              state,
              path,
              threadId,
            );
            if (!thread) {
              return undefined; // FIXME: This should fetch if the thread isn't there
            }

            const newMessage: Message = {
              attachments: payload.data.attachments,
              fromUser: payload.data.from_user,
              id: payload.data.id,
              message: payload.data.message,
              messageSource: payload.data.message_source,
              threadId: payload.data.thread_id,
              timeCreated: payload.data.time_created * 1000, // Turn seconds to ms
            };

            const messageUnreadCount =
              payload.data.from_user === Number(payload.toUserId)
                ? 0
                : thread.messageUnreadCount + 1;

            return {
              ...thread,
              messages: [...thread.messages, newMessage],
              message: newMessage,
              messageUnreadCount,
            };
          }),
          ref,
        );
      } else if (
        payload.parent_type === 'notifications' &&
        payload.type === 'groupchat'
      ) {
        const { members: membersDelta } = payload.data.payload;
        const thread = pluckListItemFromRawStoreSlice(
          state,
          path,
          payload.data.payload.thread,
        );
        if (!thread) {
          return state;
        }
        const members =
          payload.data.action === 'add'
            ? [...thread.members, ...membersDelta]
            : thread.members.filter(u => !membersDelta.includes(u));
        const otherMembers = members.filter(
          u => u !== Number(payload.toUserId),
        );
        const inactiveMembers =
          payload.data.action === 'add'
            ? thread.inactiveMembers.filter(u => !membersDelta.includes(u))
            : [...thread.inactiveMembers, ...membersDelta];

        if (members.includes(Number(payload.toUserId))) {
          // Current user is still in thread, update it.
          return mergeEntities<'threads'>(
            state,
            transformObjects([payload.data.payload.thread], threadId => ({
              ...thread,
              members,
              otherMembers,
              inactiveMembers,
            })),
            ref,
          );
        }
        // Current user was removed from thread, remove it.
        return removeEntityById<'threads'>(
          ref,
          state,
          payload.data.payload.thread,
        );
      }
      return state;
    }
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/thread/thread.reducer.ts